import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, useMap } from 'react-leaflet';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { API_URL } from '../components/config.js';
import './ProjectsMapPage.css';

// Fix for the default marker icon issue in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

// Custom icons for different project statuses
const createProjectIcon = (status) => {
  let color;
  switch (status) {
    case 'active':
      color = '#4CAF50';
      break;
    case 'completed':
      color = '#2196F3';
      break;
    case 'draft':
      color = '#FF9800';
      break;
    case 'cancelled':
      color = '#F44336';
      break;
    default:
      color = '#9E9E9E';
  }
  
  return L.divIcon({
    className: 'custom-project-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 12px;
      ">
        📍
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15]
  });
};

// Component to fit the map to project bounds
const FitBoundsToProjects = ({ projects }) => {
  const map = useMap();
  
  useEffect(() => {
    if (projects && projects.length > 0) {
      const validProjects = projects.filter(project => 
        project.location && project.location.lat && project.location.lng
      );
      
      if (validProjects.length > 0) {
        const bounds = validProjects.map(project => [
          project.location.lat,
          project.location.lng
        ]);
        
        if (bounds.length === 1) {
          // If only one project, center on it with a reasonable zoom
          map.setView(bounds[0], 13);
        } else {
          // If multiple projects, fit all bounds
          map.fitBounds(bounds, { padding: [50, 50] });
        }
      } else {
        // Default to Saskatchewan area if no projects have coordinates
        map.setView([52.9399, -106.4509], 6);
      }
    }
  }, [projects, map]);
  
  return null;
};

const ProjectsMapPage = () => {
  console.log('ProjectsMapPage component loaded!');
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mapCenter] = useState([48.3809, -89.2477]); // Thunder Bay, Ontario
  const [mapZoom] = useState(6);
  const [filter, setFilter] = useState('all');

  // Fetch projects with location data
  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/projects`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      // Parse location data from mapData field
      const projectsWithLocations = response.data.projects.map(project => {
        let location = null;
        let geoJsonData = null;
        
        if (project.mapData) {
          try {
            // Try to parse as GeoJSON first
            const geoData = JSON.parse(project.mapData);
            if (geoData.type === 'Point' && geoData.coordinates) {
              location = {
                lng: geoData.coordinates[0],
                lat: geoData.coordinates[1]
              };
            } else if (geoData.geometry && geoData.geometry.type === 'Point') {
              location = {
                lng: geoData.geometry.coordinates[0],
                lat: geoData.geometry.coordinates[1]
              };
            } else if (geoData.type === 'Feature' && geoData.geometry) {
              // Handle complex GeoJSON features (polygons, lines, etc.)
              geoJsonData = geoData;
            } else if (geoData.type && geoData.coordinates) {
              // Handle geometry objects directly
              geoJsonData = {
                type: 'Feature',
                geometry: geoData,
                properties: { name: project.name, id: project._id }
              };
            }
          } catch (e) {
            // Try to parse as simple lat,lng format
            const coords = project.mapData.split(',');
            if (coords.length === 2) {
              const lat = parseFloat(coords[0].trim());
              const lng = parseFloat(coords[1].trim());
              if (!isNaN(lat) && !isNaN(lng)) {
                location = { lat, lng };
              }
            }
          }
        }
        
        return { ...project, location, geoJsonData };
      });
      
      setProjects(projectsWithLocations);
      console.log('Projects loaded:', projectsWithLocations.length);
      console.log('Projects with point locations:', projectsWithLocations.filter(p => p.location).length);
      console.log('Projects with GeoJSON data:', projectsWithLocations.filter(p => p.geoJsonData).length);
      setError('');
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      draft: 'status-draft',
      active: 'status-active',
      completed: 'status-completed',
      cancelled: 'status-cancelled'
    };
    
    return <span className={`status-badge ${statusClasses[status]}`}>{status}</span>;
  };

  // Style function for GeoJSON features based on project status
  const getGeoJsonStyle = (feature) => {
    let color = '#9E9E9E'; // default gray
    
    // Safely check for properties and id
    if (feature && feature.properties && feature.properties.id) {
      const project = projects.find(p => p._id === feature.properties.id);
      
      if (project) {
        switch (project.status) {
          case 'active':
            color = '#4CAF50';
            break;
          case 'completed':
            color = '#2196F3';
            break;
          case 'draft':
            color = '#FF9800';
            break;
          case 'cancelled':
            color = '#F44336';
            break;
        }
      }
    }
    
    return {
      fillColor: color,
      weight: 2,
      opacity: 0.8,
      color: color,
      fillOpacity: 0.4
    };
  };

  // Filter projects based on selected filter
  const filteredProjects = projects.filter(project => {
    if (filter === 'all') return true;
    if (filter === 'with-location') return project.location !== null || project.geoJsonData !== null;
    if (filter === 'without-location') return project.location === null && project.geoJsonData === null;
    return project.status === filter;
  });

  const projectsWithLocation = filteredProjects.filter(project => project.location !== null);
  const projectsWithGeoJson = filteredProjects.filter(project => project.geoJsonData !== null);
  const projectsWithoutLocation = filteredProjects.filter(project => project.location === null && project.geoJsonData === null);

  if (loading) {
    return (
      <div className="projects-map-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading projects map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="projects-map-page">
      <div className="map-header">
        <div className="header-content">
          <div className="header-left">
            <h1>Projects Map View</h1>
            <p>Geographic overview of all consultation projects</p>
          </div>
          <div className="header-actions">
            <Link to="/staff/consultation" className="btn-back">
              ← Back to Projects
            </Link>
          </div>
        </div>
        
        {/* Filters and Stats */}
        <div className="map-controls">
          <div className="filter-section">
            <label htmlFor="status-filter">Filter by:</label>
            <select 
              id="status-filter"
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Projects</option>
              <option value="with-location">With Location</option>
              <option value="without-location">Without Location</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="draft">Draft</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div className="map-stats">
            <div className="stat-item">
              <span className="stat-number">{projectsWithLocation.length + projectsWithGeoJson.length}</span>
              <span className="stat-label">On Map</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{projectsWithoutLocation.length}</span>
              <span className="stat-label">No Location</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{filteredProjects.length}</span>
              <span className="stat-label">Total Shown</span>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="map-container">
        <MapContainer 
          center={mapCenter} 
          zoom={mapZoom} 
          className="leaflet-map"
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          
          <FitBoundsToProjects projects={projectsWithLocation} />
          
          {/* Render project markers */}
          {projectsWithLocation.map(project => (
            <Marker
              key={project._id}
              position={[project.location.lat, project.location.lng]}
              icon={createProjectIcon(project.status)}
            >
              <Popup className="project-popup">
                <div className="popup-content">
                  <h3>{project.name}</h3>
                  <div className="popup-status">
                    {getStatusBadge(project.status)}
                  </div>
                  
                  <p className="popup-description">
                    {project.description.length > 100 
                      ? `${project.description.substring(0, 100)}...`
                      : project.description
                    }
                  </p>
                  
                  <div className="popup-meta">
                    <div className="meta-item">
                      <strong>Created:</strong> {formatDate(project.createdAt)}
                    </div>
                    <div className="meta-item">
                      <strong>Comments:</strong> {project.commentCount}
                    </div>
                    {project.expectedCompletionDate && (
                      <div className="meta-item">
                        <strong>Expected Completion:</strong> {formatDate(project.expectedCompletionDate)}
                      </div>
                    )}
                  </div>
                  
                  <div className="popup-actions">
                    <Link 
                      to={`/consultation/project/${project._id}`}
                      className="btn-view-public"
                      target="_blank"
                    >
                      View Public Page
                    </Link>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Render GeoJSON shapes for projects with complex geometries */}
          {projectsWithGeoJson.map(project => (
            <GeoJSON
              key={`geojson-${project._id}`}
              data={project.geoJsonData}
              style={getGeoJsonStyle}
              onEachFeature={(feature, layer) => {
                layer.bindPopup(`
                  <div class="popup-content">
                    <h3>${project.name}</h3>
                    <div class="popup-status">
                      <span class="status-badge status-${project.status}">${project.status}</span>
                    </div>
                    <p class="popup-description">
                      ${project.description.length > 100 
                        ? project.description.substring(0, 100) + '...'
                        : project.description
                      }
                    </p>
                    <div class="popup-meta">
                      <div class="meta-item">
                        <strong>Priority:</strong> ${project.priority}
                      </div>
                      <div class="meta-item">
                        <strong>Created:</strong> ${formatDate(project.createdAt)}
                      </div>
                      ${project.expectedCompletionDate 
                        ? `<div class="meta-item">
                             <strong>Expected Completion:</strong> ${formatDate(project.expectedCompletionDate)}
                           </div>`
                        : ''
                      }
                    </div>
                    <div class="popup-actions">
                      <a href="/consultation/project/${project._id}" target="_blank" class="btn-view-public">
                        View Public Page
                      </a>
                    </div>
                  </div>
                `);
              }}
            />
          ))}
        </MapContainer>
      </div>

      {/* Projects without location */}
      {projectsWithoutLocation.length > 0 && (
        <div className="projects-without-location">
          <h2>Projects Without Location Data</h2>
          <p>These projects need location data to appear on the map:</p>
          
          <div className="unlisted-projects">
            {projectsWithoutLocation.map(project => (
              <div key={project._id} className="unlisted-project">
                <div className="project-info">
                  <h3>{project.name}</h3>
                  {getStatusBadge(project.status)}
                </div>
                <div className="project-actions">
                  <Link 
                    to="/staff/consultation"
                    className="btn-edit"
                  >
                    Add Location
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Map Legend */}
      <div className="map-legend">
        <h3>Project Status Legend</h3>
        <div className="legend-items">
          <div className="legend-item">
            <div className="legend-marker" style={{backgroundColor: '#4CAF50'}}></div>
            <span>Active</span>
          </div>
          <div className="legend-item">
            <div className="legend-marker" style={{backgroundColor: '#2196F3'}}></div>
            <span>Completed</span>
          </div>
          <div className="legend-item">
            <div className="legend-marker" style={{backgroundColor: '#FF9800'}}></div>
            <span>Draft</span>
          </div>
          <div className="legend-item">
            <div className="legend-marker" style={{backgroundColor: '#F44336'}}></div>
            <span>Cancelled</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsMapPage;